import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { AppLayoutService } from '@services/app-layout.service';
import { CurrentUserService } from '@services/current-user.service';
import { combineLatest } from 'rxjs';
import { filter, map, startWith, take } from 'rxjs/operators';

import { NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { open } from './actions/sidebar.actions';
import { State } from './reducers';
import { AnalyticsService } from './services/analytics.service';
import { ThemeService } from './services/theme.service';

@UntilDestroy()
@Component({
  selector: 'vnm-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  @ViewChild('mainContent') mainContent: ElementRef;

  sidebarEnabled$ = this.appLayout.sidebarEnabled$;
  topbarEnabled$ = this.appLayout.topbarEnabled$;
  notificationsEnabled$ = this.appLayout.notificationsEnabled$;
  noScroll$ = this.appLayout.noScroll$;
  roleSelectors$ = this.userService.user$.pipe(map(({ roles }) => roles.map((role) => `has-role-${role}`)));
  ngClass$ = combineLatest([this.sidebarEnabled$, this.roleSelectors$]).pipe(
    map(([hasSidebar, roleSelectors]) => {
      return Object.assign(
        {
          'has-sidebar': hasSidebar
        },
        ...roleSelectors.map((selector) => ({ [selector]: true }))
      );
    })
  );
  isDashboard$ = this.router.events.pipe(
    untilDestroyed(this),
    filter((event) => event instanceof NavigationEnd),
    startWith(this.router), // nav has already happened before the component is created
    map((event: NavigationEnd) => event.url === '/training' || '/selling')
  );

  constructor(
    private translateService: TranslateService,
    private analytics: AnalyticsService,
    public themeService: ThemeService,
    public userService: CurrentUserService,
    private store: Store<State>,
    private appLayout: AppLayoutService,
    private router: Router,
    @Inject(DOCUMENT) private document: Document
  ) {
    if (window.location.href.includes('sidebar=no')) {
      this.displayInsideFrame();
    }
  }

  ngOnInit() {
    this.userService.locale$.subscribe((locale) => {
      this.translateService.use(locale);
      this.document.documentElement.lang = locale;
    });

    this.userService.storefront$.pipe(take(1)).subscribe(({ analyticsOptIn, faviconPath }) => {
      const faviconElement = this.document.querySelector('link[rel=icon]') as HTMLLinkElement;
      faviconElement.href = faviconPath;

      if (analyticsOptIn) {
        this.analytics.startTracking();
      }
    });

    this.router.events
      .pipe(
        untilDestroyed(this),
        filter((event) => event instanceof NavigationEnd)
      )
      .subscribe((event) => {
        this.mainContent?.nativeElement?.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      });
  }

  onOpenSidebar(_) {
    this.store.dispatch(open());
  }

  displayInsideFrame() {
    this.appLayout.disableSidebar();
    this.appLayout.disableTopbar();
    this.useRapidClassicTheme();
  }

  useRapidClassicTheme() {
    this.themeService.updateTheme(this.themeService.activeTheme.id, {
      'color-primary': '#151f6d',
      'color-accent': '#ffc72c'
    });
  }
}
